@if (currentPaper(); as p) {
  <article
    class="group block rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:border-slate-300 md:flex md:items-center md:gap-4 md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:p-2 md:hover:bg-slate-50"
    [attr.data-paper-id]="p.id"
  >
    <!-- Left status badge column -->
    <div class="mb-2 flex items-center md:mb-0 md:w-32 md:flex-shrink-0">
      <app-status-badge [variant]="visibleStatus()"></app-status-badge>
    </div>

    <!-- Title + meta -->
    <div class="min-w-0 flex-1">
      <h3
        class="truncate text-sm font-semibold text-gray-900"
        [attr.title]="p.title"
        data-testid="paper-title"
      >
        {{ p.title }}
      </h3>
      <p class="mt-1 truncate text-xs text-gray-600" data-testid="paper-meta">
        {{ metaLine() }}
      </p>
      <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span
          class="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-700"
          [attr.data-source]="p.source"
        >
          {{ sourceLabel() }}
        </span>
        @if (workTypeLabel(); as wt) {
          <span
            class="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"
            data-testid="paper-work-type"
          >
            {{ wt }}
          </span>
        }
        @if (sourceTypeLabel(); as st) {
          <span
            class="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"
            data-testid="paper-source-type"
          >
            {{ st }}
          </span>
        }
        @if (p.hasPdf) {
          <span class="text-green-700" aria-label="Has PDF" data-testid="paper-has-pdf">PDF</span>
        } @else {
          <span
            class="inline-flex items-center gap-1 text-slate-400"
            title="No PDF available. This does not block ingestion."
            aria-label="No PDF available"
            data-testid="paper-no-pdf"
          >
            <span aria-hidden="true">&#9432;</span> no PDF
          </span>
        }
        @if (externalIdUrl(); as url) {
          <a
            [href]="url"
            target="_blank"
            rel="noopener noreferrer"
            class="truncate text-blue-600 hover:underline"
            title="OpenAlex ID"
          >
            OpenAlex
          </a>
        }
        @if (p.doi) {
          <span class="truncate text-slate-500" [attr.title]="p.doi">DOI</span>
        } @else {
          <span
            class="inline-flex items-center gap-1 text-slate-400"
            title="No DOI. This does not block ingestion."
            aria-label="No DOI"
            data-testid="paper-no-doi-info"
          >
            <span aria-hidden="true">&#9432;</span> no DOI
          </span>
        }
        @if (missingFieldsLabel(); as label) {
          <span class="text-amber-700" data-testid="paper-missing-fields">{{ label }}</span>
        }
      </div>
    </div>

    <!-- Desktop actions -->
    <div class="hidden flex-shrink-0 items-center gap-2 md:flex" data-testid="row-actions-desktop">
      @if (!isReadonly()) {
        <div
          class="flex items-center gap-2 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:focus-within:opacity-100"
        >
          <button
            type="button"
            (click)="onEdit()"
            class="btn-small-secondary"
            data-testid="row-edit-desktop"
          >
            Edit
          </button>
          <button
            type="button"
            (click)="onAttach()"
            class="btn-small-secondary"
            [title]="p.hasPdf ? 'Change PDF' : 'Attach PDF'"
            data-testid="row-attach-desktop"
          >
            {{ p.hasPdf ? 'Change PDF' : 'Attach PDF' }}
          </button>
          <button
            type="button"
            (click)="onViewAuditTrail()"
            class="btn-small-secondary"
            title="View audit trail for this paper"
          >
            History
          </button>
        </div>
      }
      @if (isReadonly()) {
        <button
          type="button"
          (click)="onViewAuditTrail()"
          class="btn-small-text md:opacity-0 md:transition-opacity md:group-hover:opacity-100"
          title="View audit trail for this paper"
        >
          History
        </button>
        <button
          type="button"
          (click)="onViewDetails()"
          class="btn-small-text md:opacity-0 md:transition-opacity md:group-hover:opacity-100"
          data-testid="row-view-details-desktop"
        >
          View details
        </button>
      }
    </div>

    <!-- Mobile: "..." toggle when editable -->
    @if (!isReadonly()) {
      <button
        type="button"
        (click)="toggleMenu()"
        class="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 md:hidden"
        aria-label="Paper actions"
        data-testid="row-menu-toggle"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="h-5 w-5"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
          />
        </svg>
      </button>
    }

    <!-- Mobile: View details (always visible, matches row spacing) -->
    @if (isReadonly()) {
      <button
        type="button"
        (click)="onViewDetails()"
        class="flex-shrink-0 text-sm font-medium text-secondary hover:text-hover md:hidden"
        data-testid="row-view-details-mobile"
      >
        View details
      </button>
    }

    <!-- Mobile-only inline action menu -->
    @if (!isReadonly() && menuOpen()) {
      <div
        class="mt-2 flex flex-col gap-1 rounded-md border border-slate-200 bg-white p-2 md:hidden"
        role="menu"
        data-testid="row-actions-mobile"
      >
        <button
          type="button"
          (click)="onEdit()"
          class="rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          role="menuitem"
          data-testid="row-edit-mobile"
        >
          Edit
        </button>
        <button
          type="button"
          (click)="onAttach()"
          class="w-full rounded-md px-3 py-2 text-left text-sm text-slate-700"
          role="menuitem"
          data-testid="row-attach-mobile"
        >
          {{ p.hasPdf ? 'Change PDF' : 'Attach PDF' }}
        </button>
        <button
          type="button"
          (click)="onViewAuditTrail()"
          class="w-full rounded-md px-3 py-2 text-left text-sm text-slate-700"
          role="menuitem"
        >
          History
        </button>
      </div>
    }
  </article>
}
